import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2, Pencil, Plus, Printer, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../lib/api";
import { hasPerm } from "../../../lib/perm";
import { fmtDate, fmtDateTime, fmtMoney } from "../../../lib/format";
import { useApi } from "../../customer/hooks";
import { StatusBadge } from "../../../components/common/StatusBadge";
import { SkeletonBlock } from "../../../components/common/EmptyState";
import { useConfirm } from "../../../components/common/ConfirmDialog";

const ERRORS = { order_not_in_review: "ops.notInReview", invalid_step: "ops.err.invalidStep", delivery_date_invalid: "checkout.dateInvalid", invalid_roles: "ops.err.invalidRoles", cannot_remove_own_owner_access: "ops.err.ownAccess" };

export const errMsg = (e, t) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.startsWith("items_unavailable")) return t("checkout.itemsUnavailable");
  return ERRORS[d] ? t(ERRORS[d]) : t("common.error");
};

const LineEditor = ({ order, items, onDone }) => {
  const { t, lang } = useI18n();
  const [lines, setLines] = useState(order.lines.map((l) => ({ item_code: l.item_code, qty_boxes: l.qty_boxes, uom: l.uom || "box" })));
  const [add, setAdd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const name = (c) => items[c]?.[`name_${lang}`] || c;
  const save = async () => {
    setBusy(true);
    try { await api.put(`/staff/orders/${order.local_id}/lines`, { lines: lines.filter((l) => l.qty_boxes > 0), reason }); onDone(); } catch (e) { toast.error(errMsg(e, t)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border-2 border-brand/40 bg-white p-4" data-testid="line-editor">
      {lines.map((l, i) => (
        <div key={`${l.item_code}:${l.uom}`} className="flex items-center gap-2 text-sm">
          <span className="min-w-0 flex-1 truncate text-navy">{l.item_code} · {name(l.item_code)} <span className="text-xs text-slate-500">({t(l.uom === "piece" ? "x.uom.piece" : "x.uom.box")})</span></span>
          <input type="number" min={0} value={l.qty_boxes} data-testid={`edit-qty-${l.item_code}`} onChange={(e) => setLines((s) => s.map((x, j) => (j === i ? { ...x, qty_boxes: Math.max(0, parseInt(e.target.value || "0", 10)) } : x)))} className="h-11 w-20 rounded-xl border border-slate-200 px-2 text-center" />
          <button type="button" aria-label="remove" onClick={() => setLines((s) => s.filter((_, j) => j !== i))} className="grid h-11 w-11 place-items-center rounded-full text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <select value={add} onChange={(e) => setAdd(e.target.value)} className="h-11 min-w-0 flex-1 rounded-xl border border-slate-200 px-2 text-sm" data-testid="edit-add-select">
          <option value="">{t("ops.addItem")}</option>
          {Object.values(items).filter((i) => !lines.some((l) => l.item_code === i.code)).map((i) => <option key={i.code} value={i.code}>{i.code} · {i[`name_${lang}`]}</option>)}
        </select>
        <button type="button" disabled={!add} onClick={() => { setLines((s) => [...s, { item_code: add, qty_boxes: 1, uom: "box" }]); setAdd(""); }} data-testid="edit-add-button" className="grid h-11 w-11 place-items-center rounded-full bg-navy text-white disabled:opacity-40"><Plus className="h-4 w-4" /></button>
      </div>
      <label className="block text-xs text-slate-500">{t("ops.reason")}<input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("ops.reasonPh")} data-testid="edit-reason-input" className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-navy" /></label>
      <div className="flex gap-2">
        <button type="button" onClick={onDone} className="h-11 flex-1 rounded-full border border-slate-200 text-sm font-semibold text-navy" data-testid="edit-cancel">{t("ops.cancel")}</button>
        <button type="button" disabled={busy || reason.trim().length < 3 || !lines.some((l) => l.qty_boxes > 0)} onClick={save} data-testid="edit-save" className="h-11 flex-1 rounded-full bg-brand text-sm font-semibold text-navy disabled:opacity-50">{t("ops.saveEdit")}</button>
      </div>
    </div>
  );
};

const Edits = ({ edits }) => {
  const { t } = useI18n();
  if (!edits.length) return null;
  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm" data-testid="edit-history">
      <h2 className="text-base font-semibold text-navy md:text-lg">{t("ops.edits")}</h2>
      <ul className="mt-2 space-y-2 text-xs text-slate-600">{edits.map((e) => <li key={e.at}><span className="font-semibold text-navy">{fmtDateTime(e.at)} · {e.by}</span> — {e.reason}</li>)}</ul>
    </section>
  );
};

export default function QueueOrder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const q = useApi("staffOrder", `/staff/orders/${id}`, undefined, { staleTime: 0 });
  const itemsQ = useApi("adminItems", "/catalogue/items");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(null);
  const items = Object.fromEntries((itemsQ.data || []).map((i) => [i.code, i]));
  const refresh = () => { qc.invalidateQueries({ queryKey: ["staffOrder"] }); qc.invalidateQueries({ queryKey: ["staffQueue"] }); window.dispatchEvent(new Event("vavlis:queue")); };

  const act = async (kind, path, body, msg) => {
    setBusy(kind);
    try { const { data } = await api.post(`/staff/orders/${id}/${path}`, body); toast.success(msg(data)); refresh(); } catch (e) { toast.error(errMsg(e, t)); } finally { setBusy(null); }
  };
  const reject = async () => {
    const message = await confirm({ title: t("ops.rejectTitle"), body: t("ops.rejectBody"), danger: true, confirmLabel: t("ops.reject"), input: { label: t("ops.rejectMessage"), minLength: 3 } });
    if (message) act("reject", "reject", { message }, () => t("orders.rejected"));
  };

  if (q.isLoading) return <SkeletonBlock className="h-96" />;
  if (!q.data) return <p className="text-sm text-slate-500">{t("orders.notFound")}</p>;
  const { order, row, edits } = q.data;
  const data = q.data;
  const review = row.status === "in_review" && hasPerm(user, "orders:review");
  const prep = hasPerm(user, "orders:prepare");

  return (
    <div className="space-y-5" data-testid="queue-order">
      {data.expired_offers?.length > 0 && <p role="alert" className="rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900" data-testid="queue-offer-expired">{t("x.offers.expiredWarn", { codes: data.expired_offers.join(", ") })}</p>}
      <button type="button" onClick={() => nav("/staff/orders")} className="flex h-11 items-center gap-1 text-sm text-slate-600 hover:text-navy" data-testid="queue-order-back"><ArrowLeft className="h-4 w-4" /> {t("shop.back")}</button>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-navy sm:text-3xl" data-testid="queue-order-title">{row.customer_name}</h1>
        <StatusBadge status={row.status} testId="queue-order-status" />
        {row.doc_no && <span className="text-sm font-semibold text-slate-600" data-testid="queue-order-doc">{row.doc_no}</span>}
        <button type="button" onClick={() => api.get(`/orders/${id}/order.pdf`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank", "noopener")).catch(() => toast.error(t("common.error")))} className="ml-auto flex h-10 items-center gap-1 rounded-full border border-slate-200 px-3 text-sm font-semibold text-navy hover:bg-slate-50" data-testid="queue-order-pdf"><Printer className="h-4 w-4" /> PDF</button>
      </div>
      <p className="text-sm text-slate-500">{row.customer_code} · {row.area_name} · {t("ops.rep")}: {row.sales_rep} · {t("ops.deliveryDate")}: {fmtDate(row.requested_delivery_date)} · {row.buyer_name}</p>
      {order.notes && <p className="rounded-2xl bg-amber-50 p-3 text-sm text-amber-900">{order.notes}</p>}
      {order.reject_message && <p className="rounded-2xl bg-rose-50 p-3 text-sm text-rose-800">{order.reject_message}</p>}
      {editing ? <LineEditor order={order} items={items} onDone={() => { setEditing(false); refresh(); }} /> : (
        <section className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between"><h2 className="text-base font-semibold text-navy md:text-lg">{t("orders.lines")}</h2>
            {review && <button type="button" onClick={() => setEditing(true)} data-testid="edit-lines-button" className="flex h-10 items-center gap-1 rounded-full px-3 text-sm font-semibold text-navy hover:bg-slate-100"><Pencil className="h-4 w-4" /> {t("ops.editLines")}</button>}</div>
          <ul className="divide-y divide-slate-100" data-testid="queue-order-lines">
            {order.lines.map((l) => <li key={`${l.item_code}:${l.uom}`} className="flex justify-between gap-3 py-2.5 text-sm"><span className="text-navy">{l.item_code} · {items[l.item_code]?.[`name_${lang}`]}</span><span className="shrink-0 tabular-nums text-slate-600">{l.qty_boxes} {t(l.uom === "piece" ? "x.uom.piece" : "x.uom.box")} × {fmtMoney(l.net_price)}</span></li>)}
          </ul>
          <p className="mt-2 text-right text-base font-bold text-navy" data-testid="queue-order-total">{fmtMoney(order.gross_total)}</p>
        </section>
      )}
      {review && !editing && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <button type="button" disabled={!!busy} onClick={reject} data-testid="queue-reject-button" className="flex h-12 items-center justify-center gap-2 rounded-full border border-rose-200 text-sm font-semibold text-rose-700 hover:bg-rose-50 sm:flex-1"><X className="h-4 w-4" /> {t("ops.reject")}</button>
          <button type="button" disabled={!!busy} onClick={() => act("approve", "approve", {}, (d) => t("ops.approvedToast", { doc: d.row.doc_no }))} data-testid="queue-approve-button" className="flex h-12 items-center justify-center gap-2 rounded-full bg-brand text-sm font-semibold text-white sm:flex-[2]">
            {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("ops.approveRelease")}
          </button>
        </div>
      )}
      {prep && ["submitted", "received", "prepared"].includes(row.status) && (
        <button type="button" disabled={!!busy} data-testid="queue-progress-button" onClick={() => act("progress", "progress", { step: row.status === "prepared" ? "loaded" : "prepared" }, () => t("admin.saved"))}
          className="h-12 w-full rounded-full bg-navy text-sm font-semibold text-white">{t(row.status === "prepared" ? "ops.markLoaded" : "ops.markPrepared")}</button>
      )}
      <Edits edits={edits} />
    </div>
  );
}
